import type { ViewContainerRef } from '@angular/core';
import { inject, Injectable } from '@angular/core';
import type { FormArray } from '@angular/forms';
import { FormControl } from '@angular/forms';

import { select, Store } from '@ngrx/store';
import { map, combineLatest } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { filterBoolean } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { LocationActions } from '@core/locations-data';
import { DialogService, DialogWidth } from '@core/user-interface-legacy';

import { LocationsActionsService } from './locations-actions.service';
import { LocationsHelperService } from './locations-helper.service';
import { LocationsAnalytics } from '../analytics/locations.analytics';
import { SelectLocationsDialogComponent } from '../dialogs/select-locations-dialog/select-locations-dialog.component';
import { SelectMode as SelectLocationsSelectMode } from '../dialogs/select-locations-dialog/select-locations-dialog.types';

@Injectable()
export abstract class AssignLocationsFacade {
  private readonly store = inject(Store);
  private readonly dialogService = inject(DialogService);
  private readonly i18n = inject(I18nService);
  private readonly locationsActionsService = inject(LocationsActionsService);
  private readonly locationsHelper = inject(LocationsHelperService);

  public jobPrimaryLanguageCode?: string;
  public jobLanguages?: string[];

  protected vcRef: ViewContainerRef;
  private locationIds: number[];
  private initialLocationIds: number[];
  private locationSlotsLimit: number;

  private analytics = inject(LocationsAnalytics);

  public execute(
    vcRef: ViewContainerRef,
    jobPrimaryLanguageCode?: string,
    jobLanguages?: string[],
  ) {
    this.vcRef = vcRef;
    this.initialLocationIds = this.locationsControl.value || [];
    this.locationIds = [...this.initialLocationIds];
    this.jobPrimaryLanguageCode = jobPrimaryLanguageCode;
    this.jobLanguages = jobLanguages;
    this.locationsControl.markAsTouched();

    this.selectLocations();
  }

  public abstract get locationsControl(): FormArray<FormControl>;
  public abstract validateLocations?(): void;
  public abstract getNewLocationOptions?(): {};

  private selectLocations(createdLocationId?: number) {
    combineLatest([this.locationsHelper.getLocationsLimit(), this.hasManageLocationsAbility$()])
      .pipe(
        take(1),
        switchMap(([locationSlotsLimit, hasManageLocationsAbility]) => {
          this.locationSlotsLimit = locationSlotsLimit;
          this.assignCreatedLocationIfPossible(createdLocationId);

          return this.openSelectLocationDialog$(hasManageLocationsAbility).afterClosed.pipe(
            filterBoolean,
            tap(newAssignedLocationIds => {
              this.fetchAssignedLocation(newAssignedLocationIds);
              this.setLocationsControl(newAssignedLocationIds);
              if (this.validateLocations) this.validateLocations();
              this.analytics.trackAssignLocation();
            }),
          );
        }),
      )
      .subscribe();
  }

  private openSelectLocationDialog$(hasManageLocationsAbility: boolean) {
    const selectMode =
      this.locationSlotsLimit > 1
        ? SelectLocationsSelectMode.Multi
        : SelectLocationsSelectMode.Single;
    return this.dialogService.openCustom(
      SelectLocationsDialogComponent,
      { width: DialogWidth.SmallX },
      {
        initialAssignedLocationIds: this.initialLocationIds,
        assignedLocationIds: this.locationIds,
        selectMode,
        locationSlotsNumber: this.locationSlotsLimit,
        hasAddNewLocation: hasManageLocationsAbility,
        hasGoToSettings: hasManageLocationsAbility,
        addNewLocationHandler$: (assignedLocationIds: number[]) =>
          this.onAddNewLocation(assignedLocationIds),
        hideInvalidLocations: true,
      },
    );
  }

  private fetchAssignedLocation(assignedLocationIds: number[]) {
    assignedLocationIds.forEach(locationId => {
      this.store.dispatch(LocationActions.fetchOne({ payload: { id: locationId } }));
    });
  }

  private onAddNewLocation(assignedLocationIds: number[]) {
    this.locationIds = [...assignedLocationIds];
    this.locationsActionsService
      .newLocation$(this.vcRef, {
        goBackHandler: () => this.selectLocations(),
        saveHandler: (createdLocationId: number) => this.selectLocations(createdLocationId),
        ...(this.getNewLocationOptions ? this.getNewLocationOptions() : {}),
      })
      .subscribe();
  }

  private hasManageLocationsAbility$() {
    return this.store.pipe(
      select(AbilitiesSelectors.abilityMap),
      map(enabledMap => enabledMap[Ability.ManageLocations]),
    );
  }

  private setLocationsControl(locationIds: number[]) {
    this.locationsControl.clear({ emitEvent: false });
    locationIds.forEach(id => {
      this.locationsControl.push(new FormControl(id), { emitEvent: false });
    });
    this.locationsControl.markAsDirty();
    this.locationsControl.updateValueAndValidity();
  }

  private assignCreatedLocationIfPossible(createdLocationId?: number) {
    const assignedLocationsNumber = this.locationIds.length;
    if (createdLocationId && this.locationSlotsLimit > assignedLocationsNumber)
      this.locationIds = [...this.locationIds, createdLocationId];
  }
}
